import React, { useState, memo } from 'react'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import BootstrapModalLayout from '../../ui/BootstrapModalLayout'
import { SSearchForm, SAddButton } from '../add-partner-admin-modal'
import Text from '../../../src/ui/Text'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../table'
import {
    useChargePointGetChargePointsQuery,
    useCompanyAddChargePointToCompanyMutation,
    ChargePointCredentials,
} from '../../common/redux/api/web-api'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import Input from '../../common/components/Input'
import { CloseIcon } from '../../common/icons/close-icon/CloseIcon'
import { Button } from '../../common/components/Button'
import useText from '../../common/hooks/useText'

export type AddCompanyChargerModalProps = {
    companyId?: string
    close: () => void
    cancel: () => void
}

export const AddCompanyChargerModal = memo(function AddCompanyChargerModal({
    companyId,
    close,
    cancel,
}: AddCompanyChargerModalProps): React.ReactElement {
    const text = useText()
    const [serialNumber, setSerialNumber] = useState('')
    const [search, setSearch] = useState(false)
    const {
        currentData: chargePointsData,

        isFetching: chargePointLoading,
    } = useChargePointGetChargePointsQuery(
        {
            id: serialNumber,
            noOfItems: 20,
        },
        { skip: !search, refetchOnMountOrArgChange: true }
    )

    const [addCharger] = useCompanyAddChargePointToCompanyMutation()

    const addChargerToCompany = async (companyId: string, chargePointCredentials: ChargePointCredentials) => {
        try {
            await addCharger({
                companyId,
                chargePointCredentials,
            })
                .unwrap()
                .then(() => notifySuccess(text('lblChargerWasAddedToOrg')))
            close()
        } catch (error) {
            notifyError(text('msgChargerNotAddedtoOrganisation'))
        }
    }

    return (
        <BootstrapModalLayout
            title={text('lblChargerAddingModal')}
            size="lg"
            titleButton={
                <Button
                    size="sm"
                    onClick={cancel}
                    primary={false}
                    className="ghost"
                    icon={<CloseIcon size={18} />}
                    data-testid="info-block-close-btn"
                />
            }
        >
            <SSearchForm>
                <Input
                    id="input-search"
                    labelText={text('lblSerialNumberShortField')}
                    value={serialNumber}
                    onChange={(event) => setSerialNumber(event.target.value)}
                    data-testid={'input-search'}
                />
                <Button
                    loading={chargePointLoading}
                    onClick={() => setSearch(true)}
                    data-testid={'btn-search'}
                    label={text('btnSearch')}
                    size="sm"
                />
            </SSearchForm>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell data-testid="table-th-serial">
                            <Text code="lblSerialNumber" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-name">
                            <Text code="lblName" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-owner">
                            <Text code="lblOwner" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-company">
                            <Text code="lblOrganisation" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-add">
                            <Text code="lblAdd" />
                        </TableHeaderCell>
                        <TableHeaderCell />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {chargePointsData &&
                        chargePointsData?.items?.map((chargePoint, index) => (
                            <TableRow key={index}>
                                <TableBodyCell data-testid={'table-th-serial-' + chargePoint.id}>
                                    {chargePoint.id}
                                </TableBodyCell>
                                <TableBodyCell data-testid={'table-th-name-' + chargePoint.id}>
                                    {chargePoint.name}
                                </TableBodyCell>
                                <TableBodyCell data-testid={'table-th-ownerId-' + chargePoint.id}>
                                    {chargePoint?.ownerName}
                                </TableBodyCell>
                                <TableBodyCell data-testid={'table-th-company-' + chargePoint.id}>
                                    {chargePoint?.companyName}
                                </TableBodyCell>
                                <TableBodyCell data-testid={'table-th-add-' + chargePoint.id}>
                                    <SAddButton
                                        tooltip={chargePoint.companyId ? text('msgChargerIsInUse') : text('btnAdd')}
                                        onClick={() =>
                                            addChargerToCompany(companyId, {
                                                id: chargePoint?.id,
                                                wifiPassword: chargePoint?.wifiPassword,
                                            })
                                        }
                                        title={<FontAwesomeIcon icon={faPlus} />}
                                        dataTestId={`AddButton-${chargePoint?.id}`}
                                        disabled={chargePoint.companyId}
                                    />
                                </TableBodyCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </BootstrapModalLayout>
    )
})
